import type { PasteSummary } from '../shared/types'
import { listPastes } from './api'
import { navigate } from './router'
import { currentUser, signOut } from './session'
import { h, snippetNodes } from './ui'

/**
 * Ctrl/Cmd-K. Jump to any pasta by name, search bodies without leaving the
 * page you're on, and reach the pages the header hides on a narrow screen.
 *
 * Installed once for the life of the tab rather than per view, so the shortcut
 * works the same everywhere including inside the editor. CodeMirror only binds
 * Shift-Mod-K (delete line), so plain Mod-K is ours to take.
 */

const SEARCH_DEBOUNCE_MS = 180
/** Below this a body search matches most of the library; titles are enough. */
const MIN_SEARCH_LENGTH = 2
const RECENT_WHEN_EMPTY = 8
/** Re-fetch the title list if the last one is older than this. */
const CACHE_TTL_MS = 30_000

interface Entry {
  label: string
  /** Right-aligned category or id. */
  hint?: string
  /** Second line — a search snippet, rendered through snippetNodes. */
  snippet?: string
  run: () => void
}

let open = false
let cached: PasteSummary[] = []
let cachedAt = 0

export function installCommandPalette(): void {
  window.addEventListener(
    'keydown',
    (event) => {
      if (event.key !== 'k' && event.key !== 'K') return
      if (!(event.metaKey || event.ctrlKey) || event.altKey || event.shiftKey) return
      event.preventDefault()
      event.stopPropagation()
      if (open) closeActive?.()
      else void openPalette()
    },
    // Capture, so CodeMirror doesn't see the key first when the editor has focus.
    true,
  )
}

let closeActive: (() => void) | null = null

export async function openPalette(): Promise<void> {
  if (open) return
  // Every entry is either one of the signed-in pages or one of your pastas.
  const user = await currentUser()
  if (!user || open) return

  open = true
  const restoreFocus = document.activeElement as HTMLElement | null

  let entries: Entry[] = []
  let selected = 0
  let query = ''
  let timer: number | null = null
  let generation = 0
  let hits: PasteSummary[] = []

  const input = h('input', {
    class: 'palette-input',
    type: 'text',
    placeholder: 'Jump to a pasta, or search inside them…',
    'aria-label': 'Command palette',
    autocomplete: 'off',
    spellcheck: 'false',
    oninput: (event: Event) => {
      query = (event.target as HTMLInputElement).value.trim()
      hits = []
      if (timer !== null) clearTimeout(timer)
      if (query.length >= MIN_SEARCH_LENGTH) {
        timer = window.setTimeout(() => void searchBodies(query), SEARCH_DEBOUNCE_MS)
      }
      render()
    },
    onkeydown: (event: Event) => onKey(event as KeyboardEvent),
  })

  const list = h('div', { class: 'palette-list', role: 'listbox', 'aria-label': 'Results' })
  const panel = h('div', { class: 'palette-panel', role: 'dialog', 'aria-modal': 'true' }, [
    input,
    list,
    h('div', { class: 'palette-foot' }, [
      h('span', {}, [h('kbd', {}, ['↑↓']), ' to move']),
      h('span', {}, [h('kbd', {}, ['↵']), ' to open']),
      h('span', {}, [h('kbd', {}, ['esc']), ' to close']),
    ]),
  ])

  const overlay = h('div', {
    class: 'palette-overlay',
    onmousedown: (event: Event) => {
      if (event.target === overlay) close()
    },
  })
  overlay.append(panel)
  document.body.append(overlay)
  input.focus()

  closeActive = close
  render()
  void refreshPastas()

  function close(): void {
    if (!open) return
    open = false
    closeActive = null
    if (timer !== null) clearTimeout(timer)
    overlay.remove()
    restoreFocus?.focus?.()
  }

  async function refreshPastas(): Promise<void> {
    if (cached.length && Date.now() - cachedAt < CACHE_TTL_MS) return
    try {
      cached = (await listPastes()).pastes
      cachedAt = Date.now()
      if (open) render()
    } catch {
      // The commands still work without the pasta list.
    }
  }

  async function searchBodies(term: string): Promise<void> {
    const mine = ++generation
    try {
      const result = await listPastes(term)
      if (!open || mine !== generation) return
      hits = result.pastes
      render()
    } catch {
      // Titles already matched what they could; leave those on screen.
    }
  }

  function build(): Entry[] {
    const needle = query.toLowerCase()

    const commands: Entry[] = [
      { label: 'New pasta', hint: 'go', run: () => navigate('/') },
      { label: 'My pastas', hint: 'go', run: () => navigate('/mine') },
      { label: 'API tokens', hint: 'go', run: () => navigate('/tokens') },
      { label: 'Passkeys', hint: 'go', run: () => navigate('/passkeys') },
      { label: 'Sign out', hint: 'account', run: () => void signOut() },
    ].filter((entry) => !needle || entry.label.toLowerCase().includes(needle))

    const byTitle = needle
      ? cached
          .map((paste) => ({ paste, at: (paste.title ?? paste.id).toLowerCase().indexOf(needle) }))
          .filter((match) => match.at !== -1)
          // An earlier match is a better one: "term" should beat "disabledTerm".
          .sort((a, b) => a.at - b.at)
          .map((match) => match.paste)
      : cached.slice(0, RECENT_WHEN_EMPTY)

    const titled = new Set(byTitle.map((paste) => paste.id))
    // Body hits the title search already covered would just be duplicates.
    const inBody = hits.filter((paste) => !titled.has(paste.id))

    return [
      ...commands,
      ...byTitle.map((paste) => toEntry(paste)),
      ...inBody.map((paste) => toEntry(paste, paste.snippet ?? undefined)),
    ]
  }

  function toEntry(paste: PasteSummary, snippet?: string): Entry {
    return {
      label: paste.title ?? 'untitled',
      hint: paste.id,
      snippet,
      run: () => navigate(`/p/${paste.id}`),
    }
  }

  function render(): void {
    entries = build()
    if (selected >= entries.length) selected = Math.max(0, entries.length - 1)

    if (!entries.length) {
      list.replaceChildren(h('div', { class: 'palette-empty' }, [`Nothing matches “${query}”.`]))
      return
    }

    list.replaceChildren(
      ...entries.map((entry, index) => {
        const option = h(
          'div',
          {
            class: `palette-option${index === selected ? ' selected' : ''}`,
            role: 'option',
            'aria-selected': index === selected,
            onmousemove: () => {
              if (selected === index) return
              selected = index
              paint()
            },
            onclick: () => activate(index),
          },
          [
            h('div', { class: 'palette-row' }, [
              h('span', { class: 'palette-label' }, [entry.label]),
              entry.hint ? h('span', { class: 'palette-hint' }, [entry.hint]) : null,
            ]),
            entry.snippet
              ? h('div', { class: 'palette-snippet' }, snippetNodes(entry.snippet))
              : null,
          ],
        )
        return option
      }),
    )
  }

  /** Selection moves far more often than the list changes; don't rebuild it. */
  function paint(): void {
    const options = [...list.children]
    options.forEach((option, index) => {
      option.classList.toggle('selected', index === selected)
      option.setAttribute('aria-selected', String(index === selected))
    })
    options[selected]?.scrollIntoView({ block: 'nearest' })
  }

  function move(delta: number): void {
    if (!entries.length) return
    selected = (selected + delta + entries.length) % entries.length
    paint()
  }

  function activate(index: number): void {
    const entry = entries[index]
    if (!entry) return
    // Close first: run() navigates, and the overlay would outlive the view.
    close()
    entry.run()
  }

  function onKey(event: KeyboardEvent): void {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault()
        move(1)
        break
      case 'ArrowUp':
        event.preventDefault()
        move(-1)
        break
      case 'Enter':
        event.preventDefault()
        activate(selected)
        break
      case 'Escape':
        event.preventDefault()
        close()
        break
      default:
        break
    }
  }
}

/** Invalidates the cached titles after a write, so the palette isn't stale. */
export function forgetPalettePastas(): void {
  cached = []
  cachedAt = 0
}
